import * as React from "react"
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Moon, Sun } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { addDays, addMonths, format, isSameDay, isWithinInterval, isBefore, startOfDay } from "date-fns"

function HolidayCalendar({
  checkInDate,
  checkOutDate,
  onDateChange,
  onClose,
  className,
  minDate = new Date(),
  maxMonths = 12,
  ...props
}) {
  const [currentMonth, setCurrentMonth] = React.useState(checkInDate || new Date());
  const [selectingCheckOut, setSelectingCheckOut] = React.useState(false);
  const [hoverDate, setHoverDate] = React.useState(null);

  const today = startOfDay(new Date());
  const maxDate = addMonths(today, maxMonths);

  // Calculate nights
  const nights = checkInDate && checkOutDate 
    ? Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24))
    : 0;

  // Generate calendar days for current month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    const days = [];

    // Previous month padding
    const prevMonth = new Date(year, month, 0);
    for (let i = startPadding - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false
      });
    }

    // Current month days
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push({
        date: new Date(year, month, day),
        isCurrentMonth: true
      });
    }

    // Next month padding (to complete 6 weeks)
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }

    return days;
  };

  const handleDayClick = (date) => {
    const clickedDate = startOfDay(date);
    
    if (isBefore(clickedDate, today) || isBefore(maxDate, clickedDate)) {
      return;
    }

    if (!checkInDate || (checkInDate && checkOutDate) || isBefore(clickedDate, checkInDate)) {
      // Start new selection
      onDateChange({ checkIn: clickedDate, checkOut: null });
      setSelectingCheckOut(true);
    } else {
      // Complete selection
      onDateChange({ checkIn: checkInDate, checkOut: clickedDate });
      setSelectingCheckOut(false);
      if (onClose) {
        setTimeout(() => onClose(), 300);
      }
    }
  };

  const handleQuickSelect = (numNights) => {
    const checkIn = addDays(today, 14);
    const checkOut = addDays(checkIn, numNights);
    onDateChange({ checkIn, checkOut });
    if (onClose) {
      setTimeout(() => onClose(), 300);
    }
  };

  const goToPreviousMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    if (newMonth >= startOfDay(new Date())) {
      setCurrentMonth(newMonth);
    }
  };

  const goToNextMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    if (newMonth <= maxDate) {
      setCurrentMonth(newMonth);
    }
  };

  const getDayClassName = (dayObj) => {
    const { date, isCurrentMonth } = dayObj;
    const dateStart = startOfDay(date);
    const isDisabled = isBefore(dateStart, today) || isBefore(maxDate, dateStart);
    const isCheckIn = checkInDate && isSameDay(dateStart, checkInDate);
    const isCheckOut = checkOutDate && isSameDay(dateStart, checkOutDate);
    const isInRange = checkInDate && checkOutDate && 
      isWithinInterval(dateStart, { start: checkInDate, end: checkOutDate });
    const isHoverRange = checkInDate && !checkOutDate && hoverDate && 
      isWithinInterval(dateStart, { 
        start: checkInDate, 
        end: isBefore(hoverDate, checkInDate) ? checkInDate : hoverDate 
      });
    const isToday = isSameDay(dateStart, today);

    let classes = "relative h-10 w-10 flex items-center justify-center text-sm transition-all duration-200 ";
    
    if (!isCurrentMonth) {
      classes += "text-slate-300 ";
    }

    if (isDisabled) {
      classes += "text-slate-300 cursor-not-allowed ";
    } else {
      classes += "cursor-pointer hover:scale-110 ";
    }

    if (isCheckIn) {
      classes += "bg-emerald-500 text-white rounded-l-full font-semibold shadow-md ";
    } else if (isCheckOut) {
      classes += "bg-blue-500 text-white rounded-r-full font-semibold shadow-md ";
    } else if (isInRange) {
      classes += "bg-emerald-50 text-emerald-800 ";
    } else if (isHoverRange && !isCheckIn) {
      classes += "bg-emerald-50/50 text-emerald-600 ";
    } else if (isToday && !isCheckIn && !isCheckOut) {
      classes += "ring-2 ring-emerald-400 ring-offset-2 rounded-full font-semibold text-emerald-600 ";
    } else if (isCurrentMonth && !isDisabled) {
      classes += "text-slate-700 hover:bg-slate-100 rounded-full ";
    }

    return classes;
  };

  const calendarDays = generateCalendarDays();
  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const canGoPrevious = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1) >= startOfDay(new Date());
  const canGoNext = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1) <= maxDate;

  return (
    <div className={cn("bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden", className)} {...props}>
      {/* Header with light gradient */}
      <div className="bg-gradient-to-r from-slate-50 via-blue-50 to-indigo-50 px-6 py-4 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-700">
            <CalendarIcon className="h-5 w-5 text-blue-600" />
            <span className="font-semibold">Select Your Dates</span>
          </div>
          {nights > 0 && (
            <div className="flex items-center gap-2 bg-emerald-100 text-emerald-700 rounded-full px-4 py-1.5">
              <Moon className="h-4 w-4" />
              <span className="font-bold text-lg">{nights}</span>
              <span className="text-sm">night{nights !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
        
        {/* Date Summary */}
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-xs text-slate-500 uppercase tracking-wide font-medium">Check-in</div>
              <div className="font-bold text-lg text-slate-800">
                {checkInDate ? format(checkInDate, 'EEE, MMM d') : 'Select date'}
              </div>
            </div>
            <div className="h-8 w-px bg-slate-200"></div>
            <div className="text-center">
              <div className="text-xs text-slate-500 uppercase tracking-wide font-medium">Check-out</div>
              <div className="font-bold text-lg text-slate-800">
                {checkOutDate ? format(checkOutDate, 'EEE, MMM d') : 'Select date'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Body */}
      <div className="p-5">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={goToPreviousMonth}
            disabled={!canGoPrevious}
            className={cn(
              "p-2 rounded-full transition-all duration-200",
              canGoPrevious 
                ? "hover:bg-slate-100 text-slate-700" 
                : "text-slate-300 cursor-not-allowed"
            )}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h2 className="text-lg font-bold text-slate-800">
            {format(currentMonth, 'MMMM yyyy')}
          </h2>
          <button
            onClick={goToNextMonth}
            disabled={!canGoNext}
            className={cn(
              "p-2 rounded-full transition-all duration-200",
              canGoNext 
                ? "hover:bg-slate-100 text-slate-700" 
                : "text-slate-300 cursor-not-allowed"
            )}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Week Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map(day => (
            <div 
              key={day} 
              className="h-10 flex items-center justify-center text-xs font-semibold text-slate-500 uppercase tracking-wider"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((dayObj, idx) => (
            <div
              key={idx}
              className={getDayClassName(dayObj)}
              onClick={() => handleDayClick(dayObj.date)}
              onMouseEnter={() => setHoverDate(startOfDay(dayObj.date))}
              onMouseLeave={() => setHoverDate(null)}
            >
              {dayObj.date.getDate()}
            </div>
          ))}
        </div>

        {/* Quick Select Buttons */}
        <div className="mt-5 pt-4 border-t border-slate-100">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-3 text-center font-medium">Quick Select</div>
          <div className="flex justify-center gap-2">
            {[7, 10, 14].map((numNights) => (
              <Button
                key={numNights}
                variant="outline"
                size="sm"
                onClick={() => handleQuickSelect(numNights)}
                className={cn(
                  "px-4 py-2 rounded-full font-medium transition-all duration-200",
                  nights === numNights
                    ? "bg-emerald-600 text-white border-transparent hover:bg-emerald-700"
                    : "border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 text-slate-700"
                )}
              >
                {numNights} nights
              </Button>
            ))}
          </div>
        </div>

        {/* Instructions */}
        {selectingCheckOut && checkInDate && !checkOutDate && (
          <div className="mt-4 text-center text-sm text-emerald-700 bg-emerald-50 rounded-lg py-2 px-4 animate-pulse">
            Now select your check-out date
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="bg-slate-50 px-5 py-3 flex items-center justify-center gap-6 text-xs text-slate-600 border-t border-slate-100">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-emerald-500"></div>
          <span>Check-in</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-blue-500"></div>
          <span>Check-out</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-emerald-100"></div>
          <span>Selected</span>
        </div>
      </div>
    </div>
  );
}

HolidayCalendar.displayName = "HolidayCalendar"

export { HolidayCalendar }
